"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"
import { Bot, User, ChevronDown, Wrench } from "lucide-react"
import { MarkdownRenderer } from "./markdown-renderer"
import { MessageActions } from "./message-actions"
import { ToolResultCard } from "./tool-result-card"
import { EntityResultCard } from "./entity-result-card"
import { ChartResultCard } from "./chart-result-card"
import { ThinkingIndicator } from "./thinking-indicator"
import type { ChatMessage as ChatMessageType, ToolCallRecord } from "@/lib/ai-chat/types"

interface ChatMessageProps {
  message: ChatMessageType
  isLast: boolean
  isStreaming?: boolean
  onRegenerate?: () => void
  onEdit?: () => void
  compact?: boolean
  conversationId?: string | null
}

export function ChatMessage({
  message,
  isLast,
  isStreaming,
  onRegenerate,
  onEdit,
  compact,
  conversationId,
}: ChatMessageProps) {
  const isUser = message.role === "user"
  const [toolsExpanded, setToolsExpanded] = useState(false)

  return (
    <div
      className={cn(
        "group flex",
        isUser ? "flex-row-reverse" : "flex-row",
        compact ? "gap-1.5 px-2 py-1" : "gap-3 px-4 py-3"
      )}
    >
      {/* Avatar — animated thinking orb when streaming, static icon otherwise */}
      {!isUser && isStreaming ? (
        <ThinkingIndicator size={compact ? "sm" : "md"} className="mt-0.5" />
      ) : (
        <div
          className={cn(
            "flex items-center justify-center rounded-full shrink-0 mt-0.5",
            compact ? "h-5 w-5" : "h-8 w-8",
            isUser ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}
        >
          {isUser
            ? <User className={compact ? "h-2.5 w-2.5" : "h-4 w-4"} />
            : <Bot className={compact ? "h-2.5 w-2.5" : "h-4 w-4"} />
          }
        </div>
      )}

      {/* Content */}
      <div
        className={cn(
          "flex flex-col min-w-0 overflow-hidden",
          compact ? "gap-0.5 max-w-[92%]" : "gap-1.5 max-w-[85%]",
          isUser ? "items-end" : "items-start"
        )}
      >
        {/* Tool calls — compact collapsible summary */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="w-full">
            <button
              type="button"
              onClick={() => setToolsExpanded((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-0.5"
            >
              <Wrench className="h-3 w-3 shrink-0" />
              <span>
                Used {message.toolCalls.length} tool{message.toolCalls.length > 1 ? "s" : ""}
              </span>
              <ChevronDown className={cn("h-3 w-3 transition-transform", toolsExpanded && "rotate-180")} />
            </button>
            {toolsExpanded && (
              <div className="ps-4 border-s border-border/50 mt-1 space-y-0">
                {message.toolCalls.map((tc: ToolCallRecord) => (
                  <ToolResultCard
                    key={tc.id}
                    toolName={tc.toolName}
                    toolLabel={tc.toolLabel}
                    status={tc.status}
                    success={tc.result?.success}
                    summary={tc.summary || (tc.result?.success ? undefined : tc.result?.error)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Message bubble */}
        {message.content && (
          <div
            className={cn(
              "overflow-hidden max-w-full",
              compact ? "rounded-xl px-2.5 py-1.5" : "rounded-2xl px-4 py-2.5",
              isUser
                ? "bg-primary text-primary-foreground rounded-ee-md"
                : "bg-muted text-foreground rounded-es-md"
            )}
          >
            {isUser ? (
              <p className={cn("whitespace-pre-wrap break-words", compact ? "text-[13px] leading-normal" : "text-sm")}>{message.content}</p>
            ) : (
              <MarkdownRenderer content={message.content} className={compact ? "pip-compact-prose" : undefined} />
            )}

            {/* Streaming cursor */}
            {isStreaming && !isUser && (
              <span className="inline-block w-2 h-4 bg-foreground/60 animate-pulse ms-0.5 -mb-0.5" />
            )}
          </div>
        )}

        {/* Rich cards for anything this turn created or changed.
            Deliberately OUTSIDE the "Used N tools" disclosure above: that
            section is collapsed by default and lists tool calls as status
            lines, which is the opposite of showing the operator the thing
            they just made. */}
        {message.toolCalls
          ?.map((tc: ToolCallRecord) => tc.entity)
          .filter((e): e is NonNullable<typeof e> => Boolean(e))
          .map((entity) => (
            <EntityResultCard key={`${entity.kind}-${entity.id}`} entity={entity} />
          ))}

        {/* A series a tool returned (vCRO wave 4): drawn in the message, on
            the same terms as the entity cards above, from the server's
            projection of the tool's own numbers. */}
        {message.toolCalls
          ?.map((tc: ToolCallRecord) => tc.chart)
          .filter((c): c is NonNullable<typeof c> => Boolean(c))
          .map((chart, i) => (
            <ChartResultCard key={`${chart.kind}-${i}`} chart={chart} />
          ))}

        {/* Actions */}
        <MessageActions
          content={message.content}
          role={message.role}
          isLast={isLast}
          messageId={message.id}
          conversationId={conversationId}
          onRegenerate={onRegenerate}
          onEdit={onEdit}
        />
      </div>
    </div>
  )
}
